<template>
  <UiBaseButtonBase v-bind="{ ...$props, ...$attrs }">
    <template
      v-for="(_, name) in $slots"
      :key="name"
      #[name]="slotData">
      <slot
        :name="name"
        v-bind="slotData ?? {}" />
    </template>
  </UiBaseButtonBase>
</template>

<script lang="ts">
import type { PropType } from 'vue'

export default {
  name: 'UiBaseButton',
  inheritAttrs: false,
  props: {
    text: { type: String, default: undefined },
    type: { type: String as PropType<'button' | 'submit'>, default: undefined },
    theme: {
      type: String as PropType<'primary' | 'neutral' | 'text' | 'alert'>,
      default: undefined,
    },
    spacingBottom: {
      type: String as PropType<'none' | 'small'>,
      default: undefined,
    },
    isFullWidth: { type: Boolean, default: undefined },
    isLoading: { type: Boolean, default: undefined },
    isDisabled: { type: Boolean, default: undefined },
    isOutlined: { type: Boolean, default: undefined },
    showSaveIcon: { type: Boolean, default: undefined },
  },
}
</script>

<style>
@reference "../../globals/css/base.css";

.s-base-button {
  border-radius: var(--button-radius);

  &.theme-primary {
    @apply bg-cta-bg text-cta-text;
    &:hover:not(.is-outlined) { background: var(--color-action-hover); }
    &:active:not(.is-outlined):not(.is-disabled):not(.is-loading) { background: var(--color-action-pressed); }
    &.is-outlined { color: var(--color-action); }
    &.is-outlined:hover { background: var(--color-action-soft-bg); }
  }

&.theme-alert {
    @apply bg-error text-white;
    &:hover:not(.is-outlined) { background: color-mix(in srgb, var(--color-feedback-error) 85%, black); }
    &.is-outlined { color: var(--color-feedback-error); }
    &.is-outlined:hover { background: var(--color-feedback-error-bg); }
  }

  &.theme-neutral {
    @apply bg-surface-muted;
    color: var(--color-action);
    &:hover:not(.is-outlined) { background: color-mix(in srgb, var(--color-surface-muted) 85%, black); }
    &.is-outlined { color: var(--color-text-secondary); }
    &.is-outlined:hover { background: var(--color-surface-sunken); }
  }

  &.theme-text {
    color: var(--color-text-secondary);
    &:hover { background: var(--color-surface-sunken); color: var(--color-text-primary); }
  }

  &.is-outlined {
    background: transparent;
    border-color: currentColor;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }

  &.is-sm {
    height: var(--button-height-sm);
    padding: 0 var(--button-padding-x-sm);
    min-width: 0;
  }
}
</style>

